"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import ProductCard from "../components/common/ProductCard"
import LoadingSpinner from "../components/common/LoadingSpinner"
import { SearchIcon } from "lucide-react"
import SEOHead from "../components/common/SEOHead"
import { collection, getDocs, query as firestoreQuery, where } from "firebase/firestore"
import { db } from "../firebase/config"

interface Product {
  id: string
  name: string
  slug?: string
  categories: string[]
  category: string
  price: number
  originalPrice?: number
  image: string
  images: string[]
  description: string
  features?: string[]
  stock: number
  rating: number
  averageRating?: number
  numRatings?: number
  totalRatings?: number
  reviews?: number
  tags?: string[]
  dimensions?: {
    length: number
    width: number
    height: number
  }
  material?: string
  warranty?: string
  isFeatured?: boolean
  isNewArrival?: boolean
  isBestSeller?: boolean
  createdAt?: string
}

const Search: React.FC = () => {
  const [searchParams] = useSearchParams()
  const query = searchParams.get("q") || ""
  const [allProducts, setAllProducts] = useState<Product[]>([])
  const [searchResults, setSearchResults] = useState<Product[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load all products from Firebase
  const loadProducts = async () => {
    try {
      setIsLoading(true)
      setError(null)

      console.log('Loading products from Firebase...');

      // Load all products from Firebase - Fixed query syntax
      const productsRef = collection(db, "products")
      const productsQuery = firestoreQuery(productsRef, where("isActive", "==", true))
      const productsSnapshot = await getDocs(productsQuery)
      const productsData: Product[] = []

      productsSnapshot.forEach((doc) => {
        const data = doc.data()
        
        // Ensure consistent data structure with proper fallbacks
        let categories: string[] = [];
        if (Array.isArray(data.categories)) {
          categories = data.categories.filter(Boolean);
        } else if (data.category && typeof data.category === 'string') {
          categories = [data.category];
        } else {
          categories = ['furniture']; // default fallback
        }
        
        // Ensure images is always an array
        let images: string[] = [];
        if (Array.isArray(data.images)) {
          images = data.images.filter(Boolean);
        } else if (data.image && typeof data.image === 'string') {
          images = [data.image];
        }
        
        // Get primary image with fallback
        const primaryImage = images[0] || data.image || data.thumbnail || "";
        
        productsData.push({
          id: doc.id,
          name: data.name || "",
          slug: data.slug || "",
          categories: categories,
          category: categories[0] || 'furniture',
          price: Number(data.price) || 0,
          originalPrice: data.originalPrice ? Number(data.originalPrice) : undefined,
          image: primaryImage,
          images: images,
          description: data.description || "",
          features: Array.isArray(data.features) ? data.features : [],
          stock: Number(data.stock) || 0,
          rating: Number(data.rating) || Number(data.averageRating) || 0,
          averageRating: Number(data.averageRating) || Number(data.rating) || 0,
          numRatings: Number(data.numRatings) || Number(data.totalRatings) || Number(data.reviews) || 0,
          totalRatings: Number(data.totalRatings) || Number(data.numRatings) || Number(data.reviews) || 0,
          reviews: Number(data.totalRatings) || Number(data.numRatings) || Number(data.reviews) || 0,
          tags: Array.isArray(data.tags) ? data.tags : [],
          dimensions: data.dimensions || undefined,
          material: data.material || undefined,
          warranty: data.warranty || undefined,
          isFeatured: Boolean(data.isFeatured),
          isNewArrival: Boolean(data.isNewArrival),
          isBestSeller: Boolean(data.isBestSeller),
          createdAt: data.createdAt || undefined,
        } as Product)
      })

      setAllProducts(productsData)
      console.log('Products loaded:', productsData.length);
    } catch (error) {
      console.error("Error loading products:", error)
      setError("Failed to load products")
    } finally {
      setIsLoading(false)
    }
  }

  // Load products on component mount
  useEffect(() => {
    loadProducts()
  }, [])

  // Filter products based on search query with improved safety checks
  useEffect(() => {
    if (query && allProducts.length > 0) {
      const results = allProducts.filter((product) => {
        const searchTerm = query.toLowerCase().trim();
        
        // Helper function to safely check string includes
        const safeIncludes = (text: string | undefined | null): boolean => {
          return text && typeof text === 'string' ? text.toLowerCase().includes(searchTerm) : false;
        };
        
        // Helper function to safely check array includes
        const safeArrayIncludes = (arr: string[] | undefined | null): boolean => {
          return Array.isArray(arr) ? arr.some(item => safeIncludes(item)) : false;
        };
        
        // Search in product name
        const nameMatch = safeIncludes(product.name);
        
        // Search in product description
        const descriptionMatch = safeIncludes(product.description);
        
        // Search in categories
        const categoryMatch = safeArrayIncludes(product.categories) || safeIncludes(product.category);
        
        // Search in tags
        const tagMatch = safeArrayIncludes(product.tags);
        
        // Search in material
        const materialMatch = safeIncludes(product.material);
        
        // Search in features
        const featureMatch = safeArrayIncludes(product.features);

        return nameMatch || descriptionMatch || categoryMatch || tagMatch || materialMatch || featureMatch;
      })
      
      setSearchResults(results)
      console.log(`Search results for "${query}":`, results.length);
    } else {
      setSearchResults([])
    }
  }, [query, allProducts])

  if (isLoading) {
    return (
      <>
        <SEOHead
          title="Search | Anmol Furniture Bazaar"
          description="Search for premium furniture at Anmol Furniture Bazaar."
        />
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-600">Loading products...</p>
          </div>
        </div>
      </>
    )
  }

  if (error) {
    return (
      <>
        <SEOHead
          title="Search Error | Anmol Furniture Bazaar"
          description="Error loading search results."
        />
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Products</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={loadProducts}
              className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <SEOHead
        title={query ? `Search results for "${query}" | Anmol Furniture Bazaar` : "Search | Anmol Furniture Bazaar"}
        description={query ? `Find products matching '${query}' at Anmol Furniture Bazaar. Shop premium furniture online.` : "Search for premium furniture at Anmol Furniture Bazaar."}
        keywords={query ? `${query}, furniture, search, premium furniture` : "furniture, search, premium furniture"}
        structuredData={query ? {
          "@context": "https://schema.org",
          "@type": "SearchResultsPage",
          "name": `Search results for '${query}'`,
          "mainEntity": searchResults.map((product) => ({
            "@type": "Product",
            "name": product.name,
            "url": `https://anmolfurniture.com/products/${product.category.toLowerCase()}/${product.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`,
            "image": product.images?.[0],
            "description": product.description,
          })),
        } : undefined}
      />
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Search Results</h1>
            <p className="text-gray-600">
              {query ? (
                <>
                  {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found for "{query}"
                </>
              ) : (
                "Enter a search term to find products"
              )}
            </p>
          </div>

          {/* Search Stats */}
          {query && (
            <div className="bg-white rounded-lg p-4 mb-6 shadow-sm">
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                <span>Searching in {allProducts.length} products</span>
                {searchResults.length > 0 && (
                  <span className="text-primary-600 font-medium">
                    {((searchResults.length / allProducts.length) * 100).toFixed(1)}% match rate
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Results */}
          {query && searchResults.length === 0 ? (
            <div className="text-center py-16">
              <SearchIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
              <p className="text-gray-600 mb-4">
                We couldn't find any products matching "{query}"
              </p>
              <div className="text-sm text-gray-500">
                <p>Try:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Using different keywords</li>
                  <li>Checking your spelling</li>
                  <li>Using more general terms</li>
                  <li>Searching by category (e.g., "sofa", "chair", "table")</li>
                </ul>
              </div>
            </div>
          ) : query === "" ? (
            <div className="text-center py-16">
              <SearchIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Start your search</h3>
              <p className="text-gray-600">Enter a search term to find products from our collection</p>
            </div>
          ) : (
            <>
              {/* Results Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {searchResults.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>

              {/* Load more or pagination could be added here if needed */}
              {searchResults.length > 0 && (
                <div className="mt-12 text-center">
                  <p className="text-gray-600">
                    Showing all {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{query}"
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default Search